import { useState, useEffect, useRef, useCallback } from 'react'
import { toast } from 'sonner'
import { apiClient, useAuthStore } from '@indipia/shared'
import type { NavFn, DirtyHandle, DirtySaveNotice } from '../pages/SchedulerPage'
import type { SiteInfo } from './CalendarMultiView'
import { MaterialOrderPopup, type MaterialOrderChange } from './MaterialOrderPopup'
import { AddTaskPopup } from './AddTaskPopup'
import { SigongPopup } from './SigongPopup'
import type { SigongTask } from './SigongPopup'
import { type ScheduleDep, type CascadePlan, formatISO, getCascadeIds, computeCascadeReschedule, validateScheduleEndWarn, validatePredecessorBlock, getPredecessorCandidates, WORK_TYPE_COLORS, SCHEDULE_VIEW_OPTIONS } from './scheduleUtils'
import { useCommonCodes } from '../context/CommonCodesContext'
import { useHolidayRange } from '../hooks/useHolidays'
import { holidayGateError, countWorkdays, addWorkdays, nextWorkday, workdaysOrNull } from './scheduleUtils'
import { useModalA11y } from '../hooks/useModalA11y'
import { useForemanAssign, type ForemanStatePatch } from '../hooks/useForemanAssign'
import { useInvalidateSignals } from '../hooks/useTaskSignals'
import { useSignalLayer } from '../hooks/useSignalLayer'
import { useScheduleOnlyCodes } from '../hooks/useScheduleOnlyCodes'
import { useSchedulerHost } from '../context/SchedulerHostContext'
import { SignalToggleChips, TodayTodoButton } from '../components/SignalControls'
import { SignalPopover } from '../components/SignalPopover'
import { SignalTodoPanel } from '../components/SignalTodoPanel'
import { SIGNAL_COLOR_LABEL, isSignalUnavailableSchedule, workStatusWritePath } from '../constants/signalView'
import { useSignalAction } from '../hooks/useSignalAction'
import { FieldReportStatusPopup } from '../components/FieldReportStatusPopup'
import { ShipStatusPopup } from '../components/ShipStatusPopup'
import { Z_POPUP, Z_CONTEXT_MENU } from '../constants/zIndex'
import { patchScheduleTaskDates, taskDateSaveErrorMessage, type DateChange, type ScheduleChangeNoticeItemResult } from '../api/foremanApi'
import { ScheduleChangeNoticeModal, notifiableDateChanges, noticeSentPatch } from '../components/ScheduleChangeNoticeModal'
import { buildTaskDeleteConfirm } from '@indipia/shared-features'
import { confirmTaskDelete } from '../hooks/confirmTaskDelete'
import { CalendarVendorCell } from '../components/material/CalendarVendorCell'
import { usePeriodSyncConfirm, periodSyncCancelledError } from '../hooks/usePeriodSyncConfirm'
import { useSiteRefresh } from '../context/SiteRefreshContext'
import { computePeriodSyncPlan, markerWarningText } from '../constants/periodSync.logic'
import {
  Button, Badge, Select, Input, Textarea, Modal, Segmented, DatePicker, Combobox, SignalDot,
  Page,                             // §15.41 — layout="fixed"(스크롤 소유권, D-07 §21.4)
  EmptyState, ErrorState, Skeleton, // §15.37 · §15.30 — 상태 UX(D-07 §17.1)
} from '@indipia/ui-core'

type Props = { navigate: NavFn; selectedSite?: SiteInfo; onDirtyChange?: (dirty: DirtyHandle | null) => void; embedded?: boolean }

interface RawTask {
  scheduleId:    number
  taskId:        number
  workTypeCode:  string
  workTypeName:  string
  taskStartDate: string
  taskEndDate:   string
  progress:      number
}

interface DetailedTask {
  taskId:          number
  workTypeCode?:   string
  workTypeName:    string
  taskStartDate:   string
  taskEndDate:     string
  progress:        number
  foremanName?:    string | null
  foremanContact?: string | null
  scheduleMemo?:   string | null
  /** 이 공정이 끝난 후 다음 공정 시작 전까지 대기해야 하는 기간(대기일) — 공정 자체의 속성 */
  leadTime?:       number
  /** 반장 상태 코드(E01_TASK_PARTNER_STATUS) */
  partnerStatus?: string | null
  sentDtm?: string | null
  respondedDtm?: string | null
  /** 배정 반장 id · 배정 출처(AUTO·MANUAL) — 시공반장 팝업 전달용 */
  assigneePartnerId?: number | null
  assignSource?: string | null
  /** 고객 요청 건수(확인 전) — 공종 삭제 확인창 안내용. 집계 실패면 null */
  requestCount?: number | null
  /** SIF-1 '자재파트너가 시공' 공종 여부('Y'/'N', 현장별 방식 override 반영 최종값) — 시공파트너 배정·일정확인 대상 제외용 */
  constructByVendorYn?: string | null
}

interface CalEvent {
  taskId:       number
  workTypeCode: string
  workTypeName: string
  startDate:    string
  endDate:      string
  color:        string
}

interface Partner {
  partnerId:      number
  partnerName:    string
  mobile?:         string
  specialtyTypes?: string | null
}

interface Milestone {
  milestoneId:   number
  milestoneType: string | null
  milestoneName: string
  milestoneDate: string
}

const COLORS = WORK_TYPE_COLORS
// 미지정(커스텀 라벨 직접 입력)일 때의 기본 마커 색 — milestoneType이 null이거나
// 알려진 코드가 아닐 때 이 색으로 표시된다 ('OTHER' 코드 자체는 더 이상 쓰지 않는다).
const MILESTONE_DEFAULT_COLOR = 'var(--shell-on-surface-2)'
const MILESTONE_COLORS: Record<string, string> = {
  MOVE_IN: 'var(--gt-primary)', PARTIAL_PAYMENT: 'var(--color-info)', COMPLETION: 'var(--color-successStrong)', INSPECTION: 'var(--indipia-purple-600)', // INSPECTION: 대응 status 토큰 없음 — ΔE 최근접 primitive. COMPLETION: 글자 대비(AA)용 Strong
}
const FREQUENT_MILESTONE_TYPES = ['MOVE_IN', 'PARTIAL_PAYMENT', 'COMPLETION', 'INSPECTION'] as const
const DEFAULT_MILESTONE_TYPE_NAMES: Record<string, string> = {
  MOVE_IN: '입주일', PARTIAL_PAYMENT: '중도금', COMPLETION: '공사완료', INSPECTION: '검사일',
}
const DN = ['일','월','화','수','목','금','토']

function pad(n: number) { return String(n).padStart(2, '0') }
function daysInMonth(year: number, month: number) { return new Date(year, month, 0).getDate() }
function firstDayOfWeek(year: number, month: number) { return new Date(year, month - 1, 1).getDay() }

function buildWeeks(year: number, month: number): (number | null)[][] {
  const total = daysInMonth(year, month)
  const first = firstDayOfWeek(year, month)
  const cells: (number | null)[] = Array(first).fill(null)
  for (let d = 1; d <= total; d++) cells.push(d)
  while (cells.length % 7 !== 0) cells.push(null)
  const weeks: (number | null)[][] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
  return weeks
}

function ds(year: number, month: number, day: number) { return `${year}-${pad(month)}-${pad(day)}` }
function overlaps(ev: CalEvent, d: string): boolean { return ev.startDate <= d && ev.endDate >= d }

// 소요일 = 작업일 수(일요일·공휴일 제외, A-09). 공휴일 정보를 쓸 수 없으면 빈 칸(달력일 수로 대체하지 않음)
function workdayCount(gate: Parameters<typeof workdaysOrNull>[0], holidays: Set<string>, start: string, end: string): number | '' {
  return workdaysOrNull(gate, holidays, new Date(start.slice(0, 10) + 'T00:00:00'), new Date(end.slice(0, 10) + 'T00:00:00')) ?? ''
}
// formatISO imported from scheduleUtils
function fmtDateStr(s: string) { return s ? s.slice(0, 10).replace(/-/g, '.') : '' }

// 시공반장 Combobox 의 배정 해제 옵션 값 — 빈 문자열은 "미선택"이라 센티넬을 따로 둔다
const PARTNER_NONE = '__none__'

// ── Trash icon ────────────────────────────────────────────────────
function TrashIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  )
}

export function CalendarScheduleView({ navigate, selectedSite, onDirtyChange, embedded }: Props) {
  const user = useAuthStore(s => s.user)
  const invalidateSignals = useInvalidateSignals()   // 날짜 저장 성공 후 서버 신호 재조회
  // 서버 신호(공종별 반장·발주·작업 3줄)·오늘 할일 + [신호등 켜기]·[빨강·노랑만] 토글·사유 팝오버·할일 패널 상태
  // 스케줄 전용 행정 공정(세대 동의서 청구 등, scheduleOnlyYn='Y')은 시공·자재가 없어 신호등·시공반장·자재거래처를 두지 않는다(간트와 같은 기준)
  const scheduleOnlyCodes = useScheduleOnlyCodes()
  // 현장 종료(거래완료)로 완료된 스케줄은 신호 조회 대상이 아니라 신호등을 켤 수 없다(간트와 같은 기준)
  const signal = useSignalLayer(selectedSite?.siteId, undefined, scheduleOnlyCodes,
    isSignalUnavailableSchedule(selectedSite?.siteDoneYn))
  const sigOn = signal.signalOn
  // 호스트(shell-web)가 주입한 이동(나의 할일·작업보고확인) — 없으면 해당 링크·버튼을 숨긴다
  const schedulerHost = useSchedulerHost()
  const now  = new Date()
  const [year]     = useState(now.getFullYear())
  const [month]    = useState(now.getMonth() + 1)
  const [day]      = useState(now.getDate())
  // 공휴일(BE `/schedule/holidays`) — 현장 기간이 연말/연초로 넘어가는 경우까지 감안해 전후 1년 조회
  // 소요일 계산·시작일 보정에 쓰므로 조회 상태를 함께 본다 — 조회 중·실패면 날짜 입력을 막고 안내한다(A-08·A-17)
  const holidayState = useHolidayRange(`${year - 1}-01-01`, `${year + 1}-12-31`)
  const holidays = holidayState.dates
  const [events,   setEvents]   = useState<CalEvent[]>([])
  const [loading,  setLoading]  = useState(false)
  const [loadError, setLoadError] = useState(false)

  // Detail panel
  const [selDay,     setSelDay]     = useState<string | null>(null)
  const [detTasks,   setDetTasks]   = useState<DetailedTask[]>([])
  const [depsMap,    setDepsMap]    = useState<Record<number, number>>({})
  const [partners,   setPartners]   = useState<Partner[]>([])
  // 시공반장 Combobox 옵션 — 공종 전문분야가 맞는 반장만 후보로 거르고 배정 해제를 위해
  // "선택 안 함"을 맨 앞에 붙인다. value ''는 "미선택"이어야 하므로 해제는 센티넬 값을 쓴다.
  const loadPartnerOptions = async (workTypeCode: string | null | undefined, assigned: boolean) => {
    const filtered = partners.filter(p =>
      !workTypeCode || !p.specialtyTypes || p.specialtyTypes.split(',').includes(workTypeCode))
    if (filtered.length === 0 && !assigned) return []
    return [
      { value: PARTNER_NONE, label: '선택 안 함' },
      ...filtered.map(p => ({ value: String(p.partnerId), label: p.partnerName })),
    ]
  }
  const [localDates, setLocalDates] = useState<Record<number, { start: string; end: string }>>({})
  const localDatesRef = useRef<Record<number, { start: string; end: string }>>({})
  const [localMemos, setLocalMemos] = useState<Record<number, string>>({})
  const [localWorkTypeNames, setLocalWorkTypeNames] = useState<Record<number, string>>({})
  const [savingTask,  setSavingTask]  = useState<number | null>(null)
  const [panelError,  setPanelError]  = useState('')
  // 당기기(시작일을 앞으로 이동) 시 후행공정을 함께 이동할지 확인하는 다이얼로그
  const [pullConfirm, setPullConfirm] = useState<{
    taskId:   number
    taskName: string
    newStart: Date
    newEnd:   Date
  } | null>(null)
  const [showAddPop,  setShowAddPop]  = useState(false)
  const [refreshKey,  setRefreshKey]  = useState(0)
  // 공종 날짜가 스케줄 기간을 넘는 저장·마커 변경 뒤 현장 정보(종료일·D-day)를 다시 읽는다
  const refreshSites = useSiteRefresh()
  const periodSync = usePeriodSyncConfirm()
  const [milestones,       setMilestones]       = useState<Milestone[]>([])
  // 자재 발주일/입고예정일 — 마일스톤처럼 캘린더에 항상 표시하기 위해 (자재&입고일 팝업과
  // 별개로) 스케줄 선택 시 미리 조회해 둔다. taskId 기준 원본 배열이며, 화면 표시 시 events에서
  // workTypeName을 조인해 쓴다.
  const [orderDates, setOrderDates] = useState<{ taskId: number; orderDate: string | null; deliveryDate: string | null }[]>([])
  const [showMilestonePop, setShowMilestonePop] = useState(false)
  const [showSigongPanel,  setShowSigongPanel]  = useState(false)
  const [showStatusPanel,  setShowStatusPanel]  = useState(false)
  const [showMaterialPanel, setShowMaterialPanel] = useState(false)
  // 자재파트너배정 팝업이 처음 선택할 공종(신호 처리 버튼) — 메뉴 진입은 null
  const [materialFocusTaskId, setMaterialFocusTaskId] = useState<number | null>(null)
  const [showShipPanel,     setShowShipPanel]     = useState(false)
  const [openDd,            setOpenDd]            = useState<'dd1' | 'dd2' | 'dd3' | null>(null)

  useEffect(() => {
    const handler = () => setOpenDd(null)
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [])

  // ── 비고 ──────────────────────────────────────────────────────
  const [scheduleRemark, setScheduleRemark] = useState('')
  const [remarkDraft,    setRemarkDraft]    = useState('')
  const [remarkSaving,   setRemarkSaving]   = useState(false)
  const [remarkHidden,   setRemarkHidden]   = useState(false)

  useEffect(() => { setScheduleRemark(''); setRemarkDraft('') }, [selectedSite?.scheduleId])

  const handleRemarkSave = async () => {
    if (!selectedSite?.scheduleId) return
    setRemarkSaving(true)
    const hdrs = {
      ...(user?.compId   ? { 'X-Comp-Id': String(user.compId)   } : {}),
      ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
    }
    try {
      await apiClient.put(`/schedule/schedules/${selectedSite.scheduleId}`, { remark: remarkDraft }, { headers: hdrs })
      setScheduleRemark(remarkDraft)
    } catch {
      toast.error('저장에 실패했습니다.')
    } finally {
      setRemarkSaving(false)
    }
  }

  // 팝업별 접근성(포커스트랩·Esc 닫기) — 상시 마운트된 채 상태 플래그로만 팝업을 토글하므로
  // 각 플래그를 open으로 그대로 넘겨 열릴 때마다 다시 걸리게 한다.
  const milestoneModal   = useModalA11y(showMilestonePop, () => setShowMilestonePop(false))

  // ── 자재파트너배정 팝업 변경 반영 — 거래처·날짜가 바뀐 공종 1건만 국소 갱신(마커용 발주·입고일) ──
  const handleMaterialChanged = useCallback((c: MaterialOrderChange) => {
    setOrderDates(prev => {
      const next = { taskId: c.taskId, orderDate: c.orderDate, deliveryDate: c.deliveryDate }
      return prev.some(o => o.taskId === c.taskId) ? prev.map(o => o.taskId === c.taskId ? next : o) : [...prev, next]
    })
  }, [])
  const openMaterialPopup = useCallback((taskId: number | null) => {
    setMaterialFocusTaskId(taskId)
    setShowMaterialPanel(true)
  }, [])

  const [mileSaving,       setMileSaving]       = useState(false)
  const [mileDeleting,     setMileDeleting]     = useState<number | null>(null)
  const [mileFormError,    setMileFormError]    = useState('')
  const [editingMsId,      setEditingMsId]      = useState<number | null>(null)
  const [mileEditForm,     setMileEditForm]     = useState<{ milestoneType: string | null; milestoneName: string; milestoneDate: string }>({ milestoneType: '', milestoneName: '', milestoneDate: '' })
  const [mileEditing,      setMileEditing]      = useState(false)
  const [mileForm,         setMileForm]         = useState<{
    milestoneType: string | null
    milestoneName: string
    milestoneDate: string
  }>({
    milestoneType: 'MOVE_IN',
    milestoneName: DEFAULT_MILESTONE_TYPE_NAMES.MOVE_IN,
    milestoneDate: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` })(),
  })

  // 간트 방식 cascade
  const depsRef          = useRef<ScheduleDep[]>([])
  const [pendingChanges, setPendingChanges] = useState<Map<number, { startDate: string; endDate: string }>>(new Map())
  const [saving,         setSaving]         = useState(false)
  // 날짜 저장 직후 변경 카톡 확인 창 — 저장 응답 dateChanges 중 notifiable 건이 있을 때만 채운다.
  const [changeNotice,   setChangeNotice]   = useState<{ scheduleId: number; changes: DateChange[] } | null>(null)
  const originalDetTasksRef = useRef<DetailedTask[]>([])
  const detTasksRef         = useRef<DetailedTask[]>([])
  const originalEventsRef   = useRef<CalEvent[]>([])
  const durationTimerRef    = useRef<Record<number, ReturnType<typeof setTimeout>>>({})
  // 응답 도착 시점에도 같은 현장을 보고 있는지 확인 — 빠른 현장 전환 시 늦은 응답이 덮어쓰는 것을 방지
  const latestScheduleIdRef = useRef<number | null>(null)

  const { codes } = useCommonCodes()
  const milestoneTypeOptions = codes?.milestoneTypes ?? []

  const headers     = user?.compId   ? { 'X-Comp-Id': String(user.compId) } : {}
  const authHeaders = { ...headers, ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}) }

  // ── Calendar events fetch ─────────────────────────────────────
  useEffect(() => {
    if (!selectedSite?.scheduleId) { latestScheduleIdRef.current = null; setEvents([]); return }
    const requestedId = selectedSite.scheduleId
    latestScheduleIdRef.current = requestedId
    const controller = new AbortController()
    setLoading(true)
    setLoadError(false)

    // 현장 전체 기간 로드
    const fromDate = selectedSite?.startDate?.slice(0, 10) ?? `${year}-${pad(month)}-01`
    const toDate   = selectedSite?.endDate?.slice(0, 10)   ?? `${year}-${pad(month)}-${pad(daysInMonth(year, month))}`

    const fetchOnce = () => apiClient
      .get<{ data: { tasks: RawTask[] } }>('/schedule/tasks/calendar', { params: { fromDate, toDate }, headers, signal: controller.signal })

    // 스케줄 생성/리셋 직후 서버 반영 지연 대비 — 점점 늘려가며 최대 3회 자동 재시도
    const CAL_RETRY_DELAYS_MS = [800, 1600, 3200]
    const fetchWithRetry = async (attempt = 0): ReturnType<typeof fetchOnce> => {
      try {
        return await fetchOnce()
      } catch (err) {
        if (controller.signal.aborted || attempt >= CAL_RETRY_DELAYS_MS.length) throw err
        await new Promise(r => setTimeout(r, CAL_RETRY_DELAYS_MS[attempt]))
        if (controller.signal.aborted) throw err
        return fetchWithRetry(attempt + 1)
      }
    }
    fetchWithRetry()
      .then(res => {
        if (latestScheduleIdRef.current !== requestedId) return
        const raw = (res.data.data?.tasks ?? []).filter(t => t.scheduleId === requestedId && t.taskStartDate && t.taskEndDate)
        const mapped = raw.map((t, i) => ({
          taskId:       Number(t.taskId),
          workTypeCode: t.workTypeCode ?? '',
          workTypeName: t.workTypeName ?? '',
          startDate:    t.taskStartDate.slice(0, 10),
          endDate:      t.taskEndDate.slice(0, 10),
          color:        COLORS[i % COLORS.length],
        }))
        setEvents(mapped)
        originalEventsRef.current = mapped
      })
      .catch((err) => {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return
        if (latestScheduleIdRef.current === requestedId) { setEvents([]); setLoadError(true) }
      })
      .finally(() => { if (latestScheduleIdRef.current === requestedId) setLoading(false) })
    return () => controller.abort()
  }, [selectedSite?.scheduleId, selectedSite?.startDate, selectedSite?.endDate, year, month, day, user?.compId, refreshKey])

  // ── Schedule detail fetch (foremanName, scheduleMemo, deps) ──
  useEffect(() => {
    if (!selectedSite?.scheduleId) { setDetTasks([]); setDepsMap({}); depsRef.current = []; setPendingChanges(new Map()); setLocalDates({}); localDatesRef.current = {}; detTasksRef.current = []; originalDetTasksRef.current = []; return }
    const requestedId = selectedSite.scheduleId
    const controller = new AbortController()
    const fetchDetailOnce = () => apiClient.get<{
      data: {
        remark?: string | null
        tasks?: { taskId: number | string; workTypeName?: string; startDate?: string; endDate?: string; partnerStatus?: string | null; progress?: number; foremanName?: string | null; foremanContact?: string | null; scheduleMemo?: string | null; leadTime?: number; sentDtm?: string | null; respondedDtm?: string | null; assigneePartnerId?: number | null; assignSource?: string | null }[]
        dependencies?: { predecessorTaskId: number | string; successorTaskId: number | string; dependencyType?: string }[]
      }
    }>(`/schedule/schedules/${selectedSite.scheduleId}`, { headers, signal: controller.signal })

    // 스케줄 생성/리셋 직후 서버 반영 지연 대비 — 점점 늘려가며 최대 3회 자동 재시도
    const DETAIL_RETRY_DELAYS_MS = [800, 1600, 3200]
    const fetchDetailWithRetry = async (attempt = 0): ReturnType<typeof fetchDetailOnce> => {
      try {
        return await fetchDetailOnce()
      } catch (err) {
        if (controller.signal.aborted || attempt >= DETAIL_RETRY_DELAYS_MS.length) throw err
        await new Promise(r => setTimeout(r, DETAIL_RETRY_DELAYS_MS[attempt]))
        if (controller.signal.aborted) throw err
        return fetchDetailWithRetry(attempt + 1)
      }
    }
    fetchDetailWithRetry()
      .then(res => {
        if (latestScheduleIdRef.current !== requestedId) return
        setScheduleRemark(res.data.data?.remark ?? '')
        setRemarkDraft(res.data.data?.remark ?? '')
        // 상세 API는 공종 기간을 startDate/endDate로 내려준다 — 화면 모델(taskStartDate/taskEndDate)로 옮긴다
        const rawTasks = (res.data.data?.tasks ?? []).map(({ startDate, endDate, ...t }) => ({ ...t, taskId: Number(t.taskId), taskStartDate: startDate ?? '', taskEndDate: endDate ?? '' })) as DetailedTask[]
        setDetTasks(rawTasks)
        detTasksRef.current = rawTasks
        originalDetTasksRef.current = rawTasks
        const rawDeps = res.data.data?.dependencies ?? []
        const m: Record<number, number> = {}
        rawDeps.forEach(d => { m[Number(d.successorTaskId)] = Number(d.predecessorTaskId) })
        setDepsMap(m)
        depsRef.current = rawDeps.map(d => ({
          predecessorTaskId: Number(d.predecessorTaskId),
          successorTaskId:   Number(d.successorTaskId),
          dependencyType:    d.dependencyType ?? 'FS',
        }))
      }).catch(() => {})
    return () => controller.abort()
  }, [selectedSite?.scheduleId, user?.compId, refreshKey])

  // ── Partners fetch ────────────────────────────────────────────
  const fetchPartners = () => {
    if (!user?.compId) return
    apiClient.get<{ data: Partner[] }>('/schedule/partners', { headers })
      .then(res => setPartners(res.data.data ?? []))
      .catch(() => {})
  }

  useEffect(() => {
    fetchPartners()
  }, [user?.compId])

  // ── Milestones fetch ──────────────────────────────────────────
  useEffect(() => {
    if (!selectedSite?.scheduleId) { setMilestones([]); return }
    apiClient.get<{ data: { milestones: Milestone[] } }>(
      `/schedule/schedules/${selectedSite.scheduleId}/milestones`, { headers }
    )
      .then(res => setMilestones(res.data.data?.milestones ?? []))
      .catch(() => {})
  }, [selectedSite?.scheduleId, user?.compId])

  // ── 자재 발주일/입고예정일 fetch (마일스톤과 동일하게 항상 조회) ──
  useEffect(() => {
    if (!selectedSite?.scheduleId) { setOrderDates([]); return }
    apiClient.get<{ data: { orders: { taskId: number; orderDate: string | null; deliveryDate: string | null }[] } }>(
      `/schedule/schedules/${selectedSite.scheduleId}/orders`, { headers }
    )
      .then(res => setOrderDates(res.data.data?.orders ?? []))
      .catch(() => {})
  }, [selectedSite?.scheduleId, user?.compId])

  // ── Milestone popup handlers ──────────────────────────────────
  const isMileInvalid = !mileForm.milestoneDate

  const handleMileAdd = async () => {
    if (!mileForm.milestoneDate) { setMileFormError('날짜를 입력하세요'); return }
    if (!selectedSite?.scheduleId) return
    const label = milestoneTypeOptions.find((o: { typeCode: string }) => o.typeCode === mileForm.milestoneType) as { typeName: string } | undefined
    const newName = mileForm.milestoneName || label?.typeName || mileForm.milestoneType || ''
    const isDup = milestones.some(m => m.milestoneDate === mileForm.milestoneDate && m.milestoneName === newName)
    if (isDup) { setMileFormError('동일한 날짜에 동일한 라벨의 마일스톤이 이미 있습니다'); return }
    setMileSaving(true); setMileFormError('')
    try {
      const res = await apiClient.post<{ data: Milestone & { warnings?: unknown } }>(
        `/schedule/schedules/${selectedSite.scheduleId}/milestones`,
        {
          milestoneType: mileForm.milestoneType,
          milestoneName: newName,
          milestoneDate: mileForm.milestoneDate,
        },
        { headers: authHeaders }
      )
      setMilestones(prev => [...prev, res.data.data])
      const addWarn = markerWarningText(res.data.data.warnings)
      if (addWarn) toast.info(addWarn)
      refreshSites()
      const defType = milestoneTypeOptions[0]?.typeCode ?? 'MOVE_IN'
      const defOpt  = milestoneTypeOptions.find((o: { typeCode: string }) => o.typeCode === defType) as { typeName: string } | undefined
      const today   = new Date()
      setMileForm({
        milestoneType: defType,
        milestoneName: defOpt?.typeName ?? DEFAULT_MILESTONE_TYPE_NAMES[defType] ?? defType,
        milestoneDate: `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`,
      })
    } catch {
      setMileFormError('저장 중 오류가 발생했습니다.')
    } finally {
      setMileSaving(false)
    }
  }

  const handleMileEditOpen = (m: Milestone) => {
    setEditingMsId(m.milestoneId)
    setMileEditForm({ milestoneType: m.milestoneType ?? '', milestoneName: m.milestoneName, milestoneDate: m.milestoneDate.slice(0, 10) })
    setMileFormError('')
  }

  // 수정·삭제가 403(권한 없음)·404(이미 삭제됐거나 이 스케줄의 마일스톤이 아님)로 거부되면 화면 목록이 서버와 어긋난 것이므로 목록을 다시 불러온다
  const MILESTONE_GONE_MSG = '이미 삭제되었거나 권한이 없는 마일스톤입니다.'
  const isDeniedOrGone = (err: unknown) => {
    const s = (err as { response?: { status?: number } })?.response?.status
    return s === 403 || s === 404
  }
  const reloadMilestones = () => {
    if (!selectedSite?.scheduleId) return
    apiClient.get<{ data: { milestones: Milestone[] } }>(
      `/schedule/schedules/${selectedSite.scheduleId}/milestones`, { headers }
    )
      .then(res => setMilestones(res.data.data?.milestones ?? []))
      .catch(() => {})
  }

  const handleMileEditSave = async (milestoneId: number) => {
    if (!selectedSite?.scheduleId || !mileEditForm.milestoneDate) { setMileFormError('날짜를 입력하세요'); return }
    setMileEditing(true); setMileFormError('')
    try {
      const label = (milestoneTypeOptions.find((o: { typeCode: string }) => o.typeCode === mileEditForm.milestoneType) as { typeName: string } | undefined)?.typeName
      const savedName = mileEditForm.milestoneName || label || mileEditForm.milestoneType || ''
      const editRes = await apiClient.patch<{ data?: { warnings?: unknown } }>(
        `/schedule/schedules/${selectedSite.scheduleId}/milestones/${milestoneId}`,
        { milestoneType: mileEditForm.milestoneType, milestoneName: savedName, milestoneDate: mileEditForm.milestoneDate },
        { headers: authHeaders }
      )
      const editWarn = markerWarningText(editRes.data?.data?.warnings)
      if (editWarn) toast.info(editWarn)
      refreshSites()
      setMilestones(prev => prev.map(m => m.milestoneId === milestoneId
        ? { ...m, milestoneType: mileEditForm.milestoneType, milestoneName: savedName, milestoneDate: mileEditForm.milestoneDate }
        : m
      ))
      setEditingMsId(null)
    } catch (err) {
      if (isDeniedOrGone(err)) {
        setMileFormError(MILESTONE_GONE_MSG)
        setEditingMsId(null)
        reloadMilestones()
      } else {
        setMileFormError('수정 중 오류가 발생했습니다.')
      }
    } finally {
      setMileEditing(false)
    }
  }

  const handleMileDelete = async (milestoneId: number) => {
    if (!selectedSite?.scheduleId) return
    setMileDeleting(milestoneId)
    try {
      const delRes = await apiClient.delete<{ data?: { warnings?: unknown } }>(`/schedule/schedules/${selectedSite.scheduleId}/milestones/${milestoneId}`, { headers })
      const delWarn = markerWarningText(delRes.data?.data?.warnings)
      if (delWarn) toast.info(delWarn)
      refreshSites()
      setMilestones(prev => prev.filter(m => m.milestoneId !== milestoneId))
    } catch (err) {
      if (isDeniedOrGone(err)) {
        toast.error(MILESTONE_GONE_MSG)
        reloadMilestones()
      } else {
        toast.error('삭제 중 오류가 발생했습니다.')
      }
    } finally {
      setMileDeleting(null)
    }
  }

  const showErr = (msg: string) => { setPanelError(msg); setTimeout(() => setPanelError(''), 3000) }

  // 인라인 콤보 배정·시공반장 팝업 결과 반영은 공용 훅(useForemanAssign)이 맡는다.
  // 확정 반장을 다른 반장으로 바꾸면 사유 모달 → partner-changes, 착공 D-2 이내면 즉시 확정 확인 모달.
  // 화면 전체를 다시 불러오면(refreshKey) 뒤 화면이 새로고침되는 것처럼 보여서, 바뀐 taskId 한 건만 detTasks(+ref)에 반영한다.
  const applyForemanPatch = (taskId: number, p: ForemanStatePatch) => {
    const merge = (t: DetailedTask) => t.taskId === taskId ? { ...t, ...p } : t
    setDetTasks(prev => prev.map(merge))
    detTasksRef.current = detTasksRef.current.map(merge)
  }
  const foremanAssign = useForemanAssign<DetailedTask>({
    partners,
    getTask: id => detTasksRef.current.find(t => t.taskId === id) ?? detTasks.find(t => t.taskId === id),
    applyPatch: applyForemanPatch,
    onError: showErr,
    headers: authHeaders,
  })
  const handleForemanAssigned = foremanAssign.applyAssigned

  // ── 신호 · 할일 처리 버튼 — 서버 주소(webPath)가 이 현장의 간트 팝업 주소(pop)면 이 화면이 가진 같은 팝업을 그 자리에서 연다.
  //    그 밖의 주소는 호스트가 이동한다(useSignalAction) ──
  const signalAction = useSignalAction((link) => {
    if (!selectedSite || (link.siteId !== null && link.siteId !== selectedSite.siteId)) return false
    if (link.pop === 'pt-assign') { setShowSigongPanel(true); return true }
    if (link.pop === 'sup-order') { openMaterialPopup(link.taskId); return true }
    if (link.pop === 'ship') { setShowShipPanel(true); return true }
    return false
  }, selectedSite?.siteId)

  // 변경 카톡 확인 창에서 발송이 성공한 공종 — 서버 응답(partnerStatus·sentDtm)으로 이 공종들만 국소 갱신.
  const handleChangeNoticeSent = (sentItems: ScheduleChangeNoticeItemResult[]) => {
    const patches = new Map<number, ReturnType<typeof noticeSentPatch>>()
    for (const it of sentItems) patches.set(it.taskId, noticeSentPatch(it))
    const merge = (list: DetailedTask[]) =>
      list.map(t => { const p = patches.get(t.taskId); return p ? { ...t, ...p } : t })
    setDetTasks(prev => merge(prev))
    detTasksRef.current = merge(detTasksRef.current)
    originalDetTasksRef.current = merge(originalDetTasksRef.current)   // [취소]가 발송 상태를 되돌리지 않게
  }

  // ── Inline: 협력업체 ──────────────────────────────────────────
  const handlePartnerChange = async (taskId: number, foremanName: string) => {
    setSavingTask(taskId)
    try { await foremanAssign.assignPartner(taskId, foremanName) } finally { setSavingTask(null) }
  }

  // ── Inline: 선행공종 ──────────────────────────────────────────
  const handlePredecessorChange = async (taskId: number, predecessorTaskId: number | null) => {
    setSavingTask(taskId)
    try {
      await apiClient.patch(`/schedule/tasks/${taskId}`, {
        predecessorTaskId, updatePredecessor: true,
      }, { headers: authHeaders })
      setDepsMap(prev => {
        const next = { ...prev }
        if (predecessorTaskId) next[taskId] = predecessorTaskId
        else delete next[taskId]
        return next
      })
      depsRef.current = [
        ...depsRef.current.filter(d => d.successorTaskId !== taskId),
        ...(predecessorTaskId ? [{ predecessorTaskId, successorTaskId: taskId, dependencyType: 'FS' }] : []),
      ]
    } catch { showErr('선행공종 저장 중 오류가 발생했습니다.') }
    finally { setSavingTask(null) }
  }

  // ── Inline: 기간 ─────────────────────────────────────────────
  // 시작일을 바꾸면 소요일(작업일 수)을 유지해 종료일을 옮긴다. 시작일이 일요일·공휴일이면 다음 작업일로 보정한다
  // (A-09·A-10·A-17 — BE 가 비작업일 시작을 거부하므로 저장 전에 맞춘다).
  const handleStartChange = (taskId: number, newStart: string, task: DetailedTask) => {
    const sd0 = new Date(newStart.slice(0, 10) + 'T00:00:00')
    const os  = new Date(task.taskStartDate.slice(0, 10) + 'T00:00:00')
    const oe  = new Date(task.taskEndDate.slice(0, 10) + 'T00:00:00')
    const preErr = holidayGateError(holidayState, sd0, os, oe)
    if (preErr) { showErr(preErr); return }
    const dur = Math.max(1, countWorkdays(os, oe, holidays))
    const sd = nextWorkday(sd0, holidays)
    const ed = addWorkdays(sd, dur, holidays)
    const endErr = holidayGateError(holidayState, sd, ed)
    if (endErr) { showErr(endErr); return }
    const entry = { start: formatISO(sd), end: formatISO(ed) }
    localDatesRef.current = { ...localDatesRef.current, [taskId]: entry }
    setLocalDates(prev => ({ ...prev, [taskId]: entry }))
  }

  // 소요일(작업일 수) 입력 — 종료일 = 시작일 포함 dur 번째 작업일. 시작일이 비작업일이면 다음 작업일로 보정한다.
  const handleDurationChange = (taskId: number, dur: number, task: DetailedTask) => {
    if (!Number.isInteger(dur) || dur < 1) return
    const baseStart = (localDatesRef.current[taskId]?.start ?? task.taskStartDate ?? '').slice(0, 10)
    if (!baseStart) return
    const bs = new Date(baseStart + 'T00:00:00')
    const preErr = holidayGateError(holidayState, bs)
    if (preErr) { showErr(preErr); return }
    const sd = nextWorkday(bs, holidays)
    const ed = addWorkdays(sd, dur, holidays)
    const endErr = holidayGateError(holidayState, sd, ed)
    if (endErr) { showErr(endErr); return }
    const entry = { start: formatISO(sd), end: formatISO(ed) }
    localDatesRef.current = { ...localDatesRef.current, [taskId]: entry }
    setLocalDates(prev => ({ ...prev, [taskId]: entry }))
    clearTimeout(durationTimerRef.current[taskId])
    durationTimerRef.current[taskId] = setTimeout(() => handleDateBlur(taskId), 400)
  }

  // events(문자열 날짜)를 캐스케이드 엔진이 쓰는 Date 기반 타입으로 변환. leadTime(대기일)은
  // events가 아니라 상세 조회(detTasksRef, /schedule/schedules/{id})에만 내려오므로 거기서 합친다.
  const eventsToTaskLike = () => events.map(ev => ({
    taskId:       ev.taskId,
    workTypeName: ev.workTypeName,
    startDate:    new Date(ev.startDate + 'T00:00:00'),
    endDate:      new Date(ev.endDate + 'T00:00:00'),
    leadTime:     detTasksRef.current.find(d => d.taskId === ev.taskId)?.leadTime ?? 0,
  }))

  // 캐스케이드 계획 적용(검증 → 커밋) — 선행 공종 미완료(validatePredecessorBlock)·공사 기간
  // 초과(validateScheduleEndWarn) 모두 변경을 막지 않고 알림만 표시한 뒤 그대로 적용한다.
  const applyCascadePlan = (plan: CascadePlan) => {
    const scheduleEnd = selectedSite?.endDate ? new Date(selectedSite.endDate.slice(0, 10) + 'T00:00:00') : null
    const tasksForValidate = eventsToTaskLike()

    const warnErr = validatePredecessorBlock(plan, tasksForValidate, depsRef.current)
      ?? validateScheduleEndWarn(plan, tasksForValidate, scheduleEnd)
    if (warnErr) showErr(warnErr)

    const newPending = new Map<number, { startDate: string; endDate: string }>()
    const newEvents = events.map(ev => {
      const p = plan.get(ev.taskId)
      if (!p) return ev
      const nsIso = formatISO(p.startDate), neIso = formatISO(p.endDate)
      newPending.set(ev.taskId, { startDate: nsIso, endDate: neIso })
      return { ...ev, startDate: nsIso, endDate: neIso }
    })

    setEvents(newEvents)
    setPendingChanges(prev => { const m = new Map(prev); newPending.forEach((v, k) => m.set(k, v)); return m })

    // detTasksRef도 동기화 (패널 표시용 foremanName 등 유지)
    const det = detTasksRef.current
    if (det.length > 0) {
      const updatedDet = det.map(t => {
        const p = newPending.get(t.taskId)
        if (!p) return t
        return { ...t, taskStartDate: p.startDate, taskEndDate: p.endDate }
      })
      detTasksRef.current = updatedDet
      setDetTasks(updatedDet)
    }
  }

  // 당기기 확인 다이얼로그 응답 처리 — 'self'는 편집한 공종만, 'cascade'는 후행공정도
  // "선행 종료일 + 선행 공정의 leadTime(대기일)" 기준으로 다시 계산해서 함께 이동한다.
  const resolvePullConfirm = (mode: 'self' | 'cascade') => {
    const pc = pullConfirm
    if (!pc) return
    setPullConfirm(null)

    if (mode === 'self') {
      applyCascadePlan(new Map([[pc.taskId, { startDate: pc.newStart, endDate: pc.newEnd }]]))
      return
    }

    const plan = computeCascadeReschedule(pc.taskId, pc.newStart, pc.newEnd, eventsToTaskLike(), depsRef.current, holidays, 'always')
    applyCascadePlan(plan)
  }

  const handleDateBlur = (taskId: number) => {
    clearTimeout(durationTimerRef.current[taskId])
    const local = localDatesRef.current[taskId]
    if (!local) return

    // events를 primary source로 사용 (항상 로드됨, 타입 일관성 보장)
    const curEv = events.find(ev => ev.taskId === taskId)
    if (!curEv) return

    const oldStart = new Date(curEv.startDate + 'T00:00:00')
    const newStart = new Date(local.start + 'T00:00:00')
    const newEnd   = new Date(local.end   + 'T00:00:00')
    const dayOffset = Math.round((newStart.getTime() - oldStart.getTime()) / 86400000)

    // 뒤로 이동해서 과거가 되는 경우만 차단 (앞으로 이동은 항상 허용)
    if (dayOffset < 0) {
      const todayMidnight = new Date(); todayMidnight.setHours(0, 0, 0, 0)
      if (newStart < todayMidnight) {
        showErr('현재 날짜 이전으로는 변경할 수 없습니다.')
        delete localDatesRef.current[taskId]
        setLocalDates(prev => { const n = { ...prev }; delete n[taskId]; return n })
        return
      }
    }

    delete localDatesRef.current[taskId]
    setLocalDates(prev => { const n = { ...prev }; delete n[taskId]; return n })

    if (dayOffset < 0) {
      // 당기기(시작일이 빨라짐) — 후행공정이 있으면 함께 이동할지 확인창을 띄우고 대기
      if (getCascadeIds(taskId, depsRef.current).length > 1) {
        setPullConfirm({ taskId, taskName: curEv.workTypeName, newStart, newEnd })
        return
      }
      applyCascadePlan(new Map([[taskId, { startDate: newStart, endDate: newEnd }]]))
      return
    }

    // 밀기(뒤로) 또는 소요일만 변경 — 확인창 없이 자동, 대기일(leadTime)을 침범할 때만
    // 후행공정도 같이 밀린다
    const plan = computeCascadeReschedule(taskId, newStart, newEnd, eventsToTaskLike(), depsRef.current, holidays, 'push-if-violated')
    applyCascadePlan(plan)
  }

  // 저장하고 변경 카톡 확인 대상을 돌려준다. "저장하고 이동"은 이 값을 부모(SchedulerPage)가 받아
  // 이동 뒤에도 확인창을 띄운다(FR-C-120). 화면의 [저장] 버튼은 아래 handleSave 가 이 화면 안에서 띄운다.
  const saveDates = async (): Promise<DirtySaveNotice | null> => {
    if (!selectedSite?.scheduleId || pendingChanges.size === 0) return null
    // 공종 날짜가 스케줄 시작·종료일을 넘으면 저장 전에 한 번만 묻는다(결정 4·5). 취소하면 저장하지 않고 변경 내용은 유지.
    const periodPlan = computePeriodSyncPlan(selectedSite, [...pendingChanges.entries()].flatMap(([taskId, dates]) => {
      const d = detTasksRef.current.find(x => x.taskId === taskId)
      return d ? [{ workTypeCode: d.workTypeCode ?? '', workTypeName: d.workTypeName, startDate: dates.startDate, endDate: dates.endDate }] : []
    }))
    if (periodPlan && !(await periodSync.confirm(periodPlan))) throw periodSyncCancelledError()
    setSaving(true)
    try {
      const { dateChanges } = await patchScheduleTaskDates(
        selectedSite.scheduleId,
        [...pendingChanges.entries()].map(([taskId, dates]) => ({ taskId, startDate: dates.startDate, endDate: dates.endDate })),
        authHeaders,
        periodPlan != null,
      )
      void invalidateSignals()
      if (periodPlan) { refreshSites(); setRefreshKey(k => k + 1) }
      originalDetTasksRef.current = detTasksRef.current
      originalEventsRef.current = events
      setPendingChanges(new Map())
      const notifiable = notifiableDateChanges(dateChanges)
      return notifiable.length > 0 ? { scheduleId: selectedSite.scheduleId, changes: notifiable } : null
    } catch (e) { showErr(taskDateSaveErrorMessage(e)); throw e }
    finally { setSaving(false) }
  }

  const handleSave = async () => {
    try {
      const notice = await saveDates()
      if (notice) setChangeNotice(notice)
    } catch { /* 저장 실패는 saveDates 가 이미 알림 — 변경 내용 유지 */ }
  }

  const handleCancel = () => {
    const orig = originalDetTasksRef.current
    detTasksRef.current = orig
    setDetTasks(orig)
    setEvents(originalEventsRef.current)
    setPendingChanges(new Map())
    localDatesRef.current = {}
    setLocalDates({})
  }

  // 저장 안 한 날짜 변경사항을 부모(SchedulerPage)에 보고 — 다른 현장 클릭·다른 페이지
  // 이동 시 저장 확인창을 띄우는 데 쓰인다. 화면을 떠날 때(unmount)는 반드시 비운다.
  useEffect(() => {
    onDirtyChange?.(pendingChanges.size > 0 ? { count: pendingChanges.size, save: saveDates, discard: handleCancel } : null)
  }, [pendingChanges, onDirtyChange, saveDates, handleCancel])
  useEffect(() => () => onDirtyChange?.(null), [onDirtyChange])

  // ── Inline: 메모 ─────────────────────────────────────────────
  const handleMemoBlur = async (taskId: number) => {
    const memo = localMemos[taskId]
    if (memo === undefined) return
    setSavingTask(taskId)
    try {
      // memo를 null로 보내면 BE(ScheduleTask.updateDetails)가 "필드 미변경"으로 해석해
      // 무시한다 — 메모를 다 지운 경우(빈 문자열)도 반드시 빈 문자열 그대로 보내야 한다.
      await apiClient.patch(`/schedule/tasks/${taskId}`, {
        scheduleMemo: memo, updatePredecessor: false,
      }, { headers: authHeaders })
      setDetTasks(prev => prev.map(t => t.taskId === taskId ? { ...t, scheduleMemo: memo } : t))
      setLocalMemos(prev => { const n = { ...prev }; delete n[taskId]; return n })
    } catch { showErr('메모 저장 중 오류가 발생했습니다.') }
    finally { setSavingTask(null) }
  }

  // ── Inline: 공종명(사장님이 주로 쓰는 이름으로 변경, 비우면 표준 공종명으로 복귀) ──
  const handleWorkTypeNameBlur = async (taskId: number) => {
    const name = localWorkTypeNames[taskId]
    if (name === undefined) return
    setSavingTask(taskId)
    try {
      await apiClient.patch(`/schedule/tasks/${taskId}`, {
        workTypeDetailName: name.trim(), updatePredecessor: false,
      }, { headers: authHeaders })
      setLocalWorkTypeNames(prev => { const n = { ...prev }; delete n[taskId]; return n })
      setRefreshKey(k => k + 1)
    } catch { showErr('공종명 저장 중 오류가 발생했습니다.') }
    finally { setSavingTask(null) }
  }

  // ── 삭제 ──────────────────────────────────────────────────────
  const handleDelete = async (taskId: number) => {
    const delTask = detTasks.find(t => t.taskId === taskId)
    if (!(await confirmTaskDelete({
      scheduleId: selectedSite?.scheduleId,
      tasks: delTask ? [delTask] : [],
      base: buildTaskDeleteConfirm({ requestCount: delTask?.requestCount }),
    }))) return
    setSavingTask(taskId)
    try {
      await apiClient.delete(`/schedule/tasks/${taskId}`, { headers })
      setDetTasks(prev => prev.filter(t => t.taskId !== taskId))
      setEvents(prev => prev.filter(ev => ev.taskId !== taskId))
    } catch { showErr('삭제 중 오류가 발생했습니다.') }
    finally { setSavingTask(null) }
  }

  const todayStr = ds(now.getFullYear(), now.getMonth() + 1, now.getDate())
  const endDt    = selectedSite?.endDate ? new Date(selectedSite.endDate) : null
  const dDiff    = endDt ? Math.round((endDt.getTime() - now.getTime()) / 86400000) : null
  const dDay     = dDiff === null ? '—' : dDiff > 0 ? `D-${dDiff}` : dDiff === 0 ? 'D-Day' : `D+${Math.abs(dDiff)}`

  // ─── Month view: 현장 전체 기간 달력 ──────────────────────────
  const renderMonth = () => {
    const siteStart = selectedSite?.startDate?.slice(0, 10)
    const siteEnd   = selectedSite?.endDate?.slice(0, 10)

    // 공사기간(현장 startDate~endDate) 밖에도 공종·마일스톤 일정이 있으면
    // 그 범위까지 달력 월을 확장한다 — ISO 날짜 문자열은 사전식 정렬이 곧 시간순 정렬이라 min/max에 그대로 사용 가능
    const allDates = [
      ...(siteStart ? [siteStart] : []),
      ...(siteEnd ? [siteEnd] : []),
      ...events.flatMap(ev => [ev.startDate, ev.endDate]),
      ...milestones.map(m => m.milestoneDate.slice(0, 10)),
    ].filter(Boolean).sort()
    const rangeStart = allDates[0]
    const rangeEnd   = allDates[allDates.length - 1]

    // 전체 기간의 모든 월 계산
    const months: { y: number; mo: number }[] = []
    if (rangeStart && rangeEnd) {
      const [sy, sm] = rangeStart.split('-').map(Number)
      const [ey, em] = rangeEnd.split('-').map(Number)
      let y = sy, mo = sm
      while (y < ey || (y === ey && mo <= em)) {
        months.push({ y, mo })
        mo++; if (mo > 12) { mo = 1; y++ }
      }
    } else {
      months.push({ y: year, mo: month })
    }

    const numMonths = months.length
    const cols = numMonths <= 1 ? 1 : numMonths === 2 ? 2 : 3
    const cellMinH = numMonths <= 2 ? 72 : 52
    const evLimit  = numMonths <= 2 ? 3 : 2
    // 폰트 하한 11px — 종전 numMonths 분기 스케일(day/ev/plus 11~7)은 11 미만으로 내려가려고만 있던 것이라 전부 11로 수렴한다.

    return (
      <div style={{ flex: 1, overflowY: 'auto', background: 'var(--shell-bg)', padding: '10px 16px 16px', minHeight: 0 }}>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 10 }}>
          {months.map(({ y, mo }) => {
            const weeks = buildWeeks(y, mo)
            return (
              <div key={`${y}-${mo}`} style={{ background: 'var(--shell-surface)', border: '1px solid var(--shell-border)', borderRadius: 12, display: 'flex', flexDirection: 'column' }}>
                {/* 월 헤더 */}
                <div style={{ background: 'var(--shell-bg)', padding: '8px 12px', borderBottom: '1px solid var(--shell-border)', fontWeight: 700, fontSize: 13, color: 'var(--shell-on-surface)', textAlign: 'center', borderRadius: '12px 12px 0 0', flexShrink: 0 }}>
                  {y}년 {mo}월
                </div>
                {/* flex:1 — 같은 줄의 월 카드끼리 이벤트 개수와 무관하게 높이가 맞춰지도록(그리드
                    기본 stretch로 카드 자체는 이미 같은 높이가 되므로), 안쪽 날짜 그리드도 그 늘어난
                    높이를 그대로 채우게 해서 이벤트가 적은 달의 행이 유독 얇아 보이지 않게 한다. */}
                <div style={{ padding: '6px 6px 8px', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  {/* 인디플래너 홈(CalendarMultiView) 월 그리드와 동일한 격자선 스타일 —
                      요일 헤더+날짜 셀을 하나의 grid로 합치고, 바깥은 top/left, 각 셀은
                      bottom/right 테두리만 둬서 선이 하나로 이어지도록 한다(gap 없음, 카드형 아님). */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: `auto repeat(${weeks.length}, 1fr)`, borderTop: '1px solid var(--shell-border)', borderLeft: '1px solid var(--shell-border)', overflow: 'hidden', flex: 1 }}>
                    {DN.map((d, i) => (
                      <div key={d} style={{ textAlign: 'center', fontSize: 'var(--type-caption-size)', fontWeight: 700, color: i === 0 ? 'var(--status-danger-600)' : i === 6 ? 'var(--status-info-600)' : 'var(--shell-on-surface-2)', padding: '4px 0', borderBottom: '1px solid var(--shell-border)', borderRight: '1px solid var(--shell-border)' }}>{d}</div>
                    ))}
                    {weeks.flat().map((dayNum, idx) => {
                      const col     = idx % 7
                      const dateStr = dayNum ? ds(y, mo, dayNum) : ''
                      // [빨강·노랑만] — 신호등이 켜져 있을 때 빨강·노랑 신호가 있는 공종만 그린다
                      const dayEvs  = dayNum ? events.filter(ev => overlaps(ev, dateStr) && !(sigOn && signal.onlyRY && !signal.ryTaskIds.has(ev.taskId))) : []
                      const ghostEvs = (dayNum && pendingChanges.size > 0)
                        ? originalEventsRef.current.filter(origEv => {
                            if (!overlaps(origEv, dateStr)) return false
                            const curEv = events.find(e => e.taskId === origEv.taskId)
                            return !!(curEv && !overlaps(curEv, dateStr))
                          }).map(ev => ({ ...ev, isGhost: true as const }))
                        : []
                      const allEvs: (CalEvent & { isGhost: boolean })[] = [
                        ...ghostEvs,
                        ...dayEvs.map(ev => ({ ...ev, isGhost: false })),
                      ]
                      const cellFull = allEvs.length >= evLimit
                      const isToday = dateStr === todayStr
                      const isSel   = dateStr === selDay
                      return (
                        <div
                          key={idx}
                          role={dayNum ? 'button' : undefined}
                          tabIndex={dayNum ? 0 : undefined}
                          onClick={() => { if (dayNum) setSelDay(isSel ? null : dateStr) }}
                          onKeyDown={e => { if (dayNum && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setSelDay(isSel ? null : dateStr) } }}
                          style={{
                            background: dayNum ? (isSel ? 'var(--gt-subtle)' : 'var(--shell-surface)') : 'var(--shell-bg)',
                            padding: '3px 3px', minHeight: cellMinH, minWidth: 0,
                            display: 'flex', flexDirection: 'column', gap: 1,
                            borderBottom: '1px solid var(--shell-border)', borderRight: '1px solid var(--shell-border)',
                            cursor: dayNum ? 'pointer' : 'default',
                            transition: 'background .12s',
                          }}
                        >
                          {dayNum && (
                            <div style={{ height: 17, display: 'flex', alignItems: 'center', marginBottom: 1, padding: '0 1px' }}>
                              {isToday
                                ? <div style={{ width: 15, height: 15, borderRadius: '50%', background: 'var(--gt-primary)', color: 'var(--shell-surface)', fontSize: 'var(--type-caption-size)', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{dayNum}</div>
                                : isSel
                                  ? <div style={{ width: 15, height: 15, borderRadius: '50%', background: 'var(--gt-subtle)', color: 'var(--gt-primary)', fontSize: 'var(--type-caption-size)', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{dayNum}</div>
                                  : <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 400, color: (col === 0 || holidays.has(dateStr)) ? 'var(--status-danger-600)' : col === 6 ? 'var(--status-info-600)' : 'var(--shell-on-surface)' }}>{dayNum}</span>
                              }
                            </div>
                          )}
                          {allEvs.slice(0, evLimit).map(ev => {
                            if (ev.isGhost) {
                              return (
                                <div key={`g-${ev.taskId}`} style={{ borderRadius: 4, padding: '1px 4px', fontSize: 'var(--type-caption-size)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', background: 'var(--shell-skeleton)', color: 'var(--shell-on-surface-2)', border: '1px dashed var(--shell-hint)', opacity: 0.7, textDecoration: 'line-through' }}>
                                  {ev.workTypeName}
                                </div>
                              )
                            }
                            const evPending = pendingChanges.has(ev.taskId)
                            const evSigOn = sigOn && !scheduleOnlyCodes.has(ev.workTypeCode)
                            // 신호등 — 공종 시작일·주 첫 칸 막대 앞에 대표색 점 1개(흰색·빗금 제외, 모두 흰색이면 점 없음).
                            // 서버 신호가 없거나 조회가 실패하면 점 대신 오류 표시(폴백 없음).
                            const showDot = evSigOn && !signal.signalsQ.isLoading && (ev.startDate.slice(0, 10) === dateStr || col === 0)
                            const evSignal = signal.byTask.get(ev.taskId)
                            const dotFailed = signal.signalsQ.isError || !evSignal
                            const repColor = evSignal?.representativeColorCode ?? null
                            return (
                              <div
                                key={ev.taskId}
                                role={evSigOn ? 'button' : undefined}
                                tabIndex={evSigOn ? 0 : undefined}
                                aria-label={evSigOn ? `${ev.workTypeName} 신호 사유 보기` : undefined}
                                onClick={evSigOn ? e => {
                                  e.stopPropagation()
                                  if (signal.popover?.taskId === ev.taskId) { signal.closePopover(); return }
                                  const r = e.currentTarget.getBoundingClientRect()
                                  signal.setPopover({ taskId: ev.taskId, anchor: { x: r.left, y: r.bottom + 6 } })
                                } : undefined}
                                onKeyDown={evSigOn ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); signal.setPopover({ taskId: ev.taskId, anchor: { x: r.left, y: r.bottom + 6 } }) } } : undefined}
                                style={{ borderRadius: 4, padding: '1px 4px', fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-surface)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', background: ev.color, outline: evPending ? '1.5px dashed var(--alpha-white-900)' : 'none', outlineOffset: '-1px', opacity: evPending ? 0.85 : 1, boxShadow: evPending ? `0 0 0 1.5px ${ev.color}` : 'none', cursor: evSigOn ? 'pointer' : undefined }}
                              >
                                {showDot && dotFailed && (
                                  <span role="img" aria-label="신호 오류" title="신호 정보를 불러오지 못했어요" style={{ display: 'inline-block', width: 10, height: 10, marginRight: 4, borderRadius: '50%', background: 'var(--status-danger-600)', color: 'var(--shell-surface)', fontSize: 8, fontWeight: 700, lineHeight: '10px', textAlign: 'center', verticalAlign: 'middle', boxShadow: '0 0 0 1.5px var(--shell-surface)' }}>!</span>
                                )}
                                {showDot && !dotFailed && repColor && (
                                  <SignalDot color={repColor} label={`${ev.workTypeName} 대표 신호 ${SIGNAL_COLOR_LABEL[repColor]}`} style={{ marginRight: 4, verticalAlign: 'middle', boxShadow: '0 0 0 1.5px var(--shell-surface)' }} />
                                )}
                                {ev.workTypeName}
                              </div>
                            )
                          })}
                          {allEvs.length > evLimit && <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>+{allEvs.length - evLimit}개</div>}
                          {/* 공종 칸(evLimit)이 이미 꽉 찼으면 마일스톤·발주/입고 마커는 숨긴다 */}
                          {dayNum && !cellFull && milestones
                            .filter(m => m.milestoneDate.slice(0, 10) === dateStr)
                            .map(m => {
                              const mc = MILESTONE_COLORS[m.milestoneType ?? ''] ?? MILESTONE_DEFAULT_COLOR
                              return (
                                <div key={`ms-${m.milestoneId}`} style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 'var(--type-caption-size)', fontWeight: 700, color: mc, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                  <span style={{ flexShrink: 0 }}>◆</span>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.milestoneName}</span>
                                </div>
                              )
                            })
                          }
                          {dayNum && !cellFull && orderDates
                            .filter(o => o.orderDate?.slice(0, 10) === dateStr)
                            .map(o => {
                              const wtName = events.find(ev => ev.taskId === o.taskId)?.workTypeName ?? ''
                              return (
                                <div key={`ord-${o.taskId}`} style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--gt-primary)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                  <span style={{ flexShrink: 0 }}>◆</span>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{wtName} 발주</span>
                                </div>
                              )
                            })
                          }
                          {dayNum && !cellFull && orderDates
                            .filter(o => o.deliveryDate?.slice(0, 10) === dateStr)
                            .map(o => {
                              const wtName = events.find(ev => ev.taskId === o.taskId)?.workTypeName ?? ''
                              return (
                                <div key={`del-${o.taskId}`} style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                  <span style={{ flexShrink: 0 }}>●</span>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{wtName} 입고</span>
                                </div>
                              )
                            })
                          }
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // ─── Bottom edit panel ────────────────────────────────────
  const renderBottomPanel = () => {
    if (!selDay) return null

    // 현재 이벤트 기준으로 패널 공종 목록 결정 (cascade 이후 새 날짜에서 편집 가능)
    const baseEvts = events.filter(ev => overlaps(ev, selDay))
    const selDayTasks: DetailedTask[] = baseEvts.map(ev => {
      const det = detTasks.find(d => Number(d.taskId) === Number(ev.taskId))
      return {
        taskId:        ev.taskId,
        workTypeCode:  ev.workTypeCode,
        workTypeName:  ev.workTypeName ?? '',
        taskStartDate: ev.startDate,
        taskEndDate:   ev.endDate,
        progress:      det?.progress      ?? 0,
        foremanName:   det?.foremanName   ?? null,
        foremanContact: det?.foremanContact ?? null,
        scheduleMemo:  det?.scheduleMemo  ?? null,
        constructByVendorYn: det?.constructByVendorYn ?? null,
      }
    })

    // 선행공종 후보는 "화면(리스트)에 위→아래로 나열되는 순서" = 시작일순을 기준으로 판단한다.
    const orderedDetTasks = [...detTasks].sort((a, b) => (a.taskStartDate ?? '').localeCompare(b.taskStartDate ?? ''))
    const selDayMiles     = milestones.filter(m => m.milestoneDate.slice(0, 10) === selDay)
    const selDayOrders    = orderDates.filter(o => o.orderDate?.slice(0, 10) === selDay)
    const selDayDeliveries = orderDates.filter(o => o.deliveryDate?.slice(0, 10) === selDay)
    const [selY, selM, selD] = selDay.split('-').map(Number)
    // 공종 배지는 고정폭(116px)이라 1fr로 두면 뒤쪽에 불필요한 여백이 생김 —
    // 배지+패딩에 딱 맞는 고정폭으로 바꾸고, 남는 만큼 메모 칼럼에 더해줌.
    // 선행공종도 옵션명이 잘리지 않도록 고정폭(140px)으로 넉넉하게 확보.
    // ui-core 컨트롤(높이 36·14px)이 들어가므로 각 칼럼에 최소폭을 보장한다 —
    // 최소폭 합계를 넘기는 좁은 화면에서는 아래 컨테이너가 가로로 스크롤된다.
    // 자재거래처 칸 — 간트 상세창 자재파트너 카드와 같은 거래처 지정·변경(발주 없는 공종은 '발주 없음')
    const COLS = '124px 140px minmax(120px, 1fr) minmax(150px, 1.2fr) minmax(190px, 1.5fr) minmax(140px, 2.4fr) 32px'

    return (
      <div style={{ flexShrink: 0, height: 272, borderTop: 'var(--border-width-hairline) solid var(--shell-border)', background: 'var(--shell-bg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '7px 16px', borderBottom: '1px solid var(--shell-border)', background: 'var(--shell-surface)', flexShrink: 0, gap: 8 }}>
          <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>{selY}년 {selM}월 {selD}일 공종</span>
          <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', background: 'var(--shell-border)', padding: '1px 7px', borderRadius: 12, fontWeight: 700 }}>{selDayTasks.length + selDayMiles.length + selDayOrders.length + selDayDeliveries.length}개</span>
          {panelError && <div className="field error" role="alert"><span className="help">{panelError}</span></div>}
          <button
            onClick={() => setSelDay(null)}
            aria-label="닫기"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: 'var(--shell-on-surface-2)', fontSize: 15, lineHeight: 1 }}
          >
            ✕
          </button>
        </div>

        {pendingChanges.size > 0 && (
          <div style={{ background: 'color-mix(in srgb, var(--color-warning) 10%, white)', borderBottom: '1px solid color-mix(in srgb, var(--color-warning) 25%, white)', padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--gt-primary)', flex: 1 }}>{pendingChanges.size}개 공종 날짜 변경됨</span>
            <Button variant="secondary" size="sm" onClick={handleCancel}>취소</Button>
            <Button variant="primary" size="sm" loading={saving} onClick={handleSave}>
              {saving ? '저장 중...' : '저장'}
            </Button>
          </div>
        )}

        {selDayTasks.length === 0 && selDayMiles.length === 0 && selDayOrders.length === 0 && selDayDeliveries.length === 0 ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--shell-hint)', fontSize: 12 }}>
            이 날짜에 계획된 작업공종이 없습니다
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
            {/* Column headers */}
            {selDayTasks.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: COLS, padding: '4px 12px', borderBottom: '1px solid var(--shell-border)', background: 'var(--shell-bg)', position: 'sticky', top: 0, zIndex: 1 }}>
                {['공종', '선행공종', '시공반장', '자재거래처', '기간', '메모', ''].map((h, i) => (
                  <div key={i} style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)', padding: '2px 4px' }}>{h}</div>
                ))}
              </div>
            )}

            {selDayTasks.map(task => {
              const evColor    = events.find(ev => ev.taskId === task.taskId)?.color ?? 'var(--shell-on-surface-2)'
              const predId     = depsMap[Number(task.taskId)]
              const isSaving   = savingTask === task.taskId
              const isPending  = pendingChanges.has(task.taskId)
              const localStart = (localDates[task.taskId]?.start ?? task.taskStartDate ?? '').slice(0, 10)
              const localEnd   = (localDates[task.taskId]?.end   ?? task.taskEndDate   ?? '').slice(0, 10)
              const memoVal    = localMemos[task.taskId] !== undefined ? localMemos[task.taskId] : (task.scheduleMemo ?? '')
              const workTypeNameVal = localWorkTypeNames[task.taskId] !== undefined ? localWorkTypeNames[task.taskId] : task.workTypeName

              return (
                <div
                  key={task.taskId}
                  style={{
                    display: 'grid', gridTemplateColumns: COLS,
                    padding: '7px 12px', borderBottom: `1px solid ${isPending ? 'var(--gt-subtle)' : 'var(--shell-border)'}`,
                    alignItems: 'center', opacity: isSaving ? 0.55 : 1,
                    transition: 'opacity .15s, background .15s', background: isPending ? 'var(--shell-bg)' : 'var(--shell-surface)',
                  }}
                >
                  {/* 공종 — 사장님이 주로 쓰는 이름으로 변경 가능(비우면 표준 공종명으로 복귀) */}
                  <div style={{ padding: '0 4px', display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                    <Input
                      value={workTypeNameVal}
                      disabled={isSaving}
                      aria-label="공종명"
                      title="공종명을 사장님이 주로 쓰는 이름으로 바꿀 수 있습니다 — 비우면 표준 공종명으로 돌아갑니다"
                      onChange={e => setLocalWorkTypeNames(prev => ({ ...prev, [task.taskId]: e.target.value }))}
                      onBlur={() => handleWorkTypeNameBlur(task.taskId)}
                      className="!max-w-none"
                      style={{ background: `${evColor}18`, color: evColor, fontSize: 'var(--type-caption-size)', fontWeight: 700, padding: '3px 9px', borderRadius: 12, border: `1px solid ${evColor}40`, minWidth: 0, width: 116, textAlign: 'center' }}
                    />
                    {isPending && <span style={{ fontSize: 'var(--type-caption-size)', background: 'var(--gt-primary)', color: 'var(--shell-surface)', borderRadius: 4, padding: '1px 5px', fontWeight: 700, flexShrink: 0 }}>변경</span>}
                  </div>

                  {/* 선행공종 */}
                  <div style={{ padding: '0 4px' }}>
                    <Select
                      value={predId ?? ''}
                      disabled={isSaving}
                      onChange={e => handlePredecessorChange(task.taskId, e.target.value ? Number(e.target.value) : null)}
                      className="!h-auto !py-0.5 !mb-0 text-[length:var(--type-caption-size)]"
                    >
                      <option value="">없음</option>
                      {getPredecessorCandidates(task.taskId, orderedDetTasks, depsRef.current).map(t => (
                        <option key={t.taskId} value={t.taskId}>{t.workTypeName}</option>
                      ))}
                    </Select>
                  </div>

                  {/* 시공반장 — 리스트 뷰와 동일한 ui-core Combobox. 스케줄 전용 공정은 시공이 없어 '해당 없음' */}
                  <div style={{ padding: '0 4px' }}>
                    {scheduleOnlyCodes.has(task.workTypeCode ?? '') ? (
                      <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)' }}>해당 없음</span>
                    ) : task.constructByVendorYn === 'Y' ? (
                      <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>자재파트너가 시공해요</span>
                    ) : (
                      <Combobox
                        value={partners.find(p => p.partnerName === task.foremanName)
                          ? String(partners.find(p => p.partnerName === task.foremanName)!.partnerId) : ''}
                        displayValue={task.foremanName ?? ''}
                        disabled={isSaving}
                        loadOptions={() => loadPartnerOptions(task.workTypeCode, !!task.foremanName)}
                        emptyText="등록된 시공반장이 없습니다"
                        onChange={(val, opt) => handlePartnerChange(task.taskId, val === PARTNER_NONE ? '' : opt.label)}
                        className="!max-w-none"
                      />
                    )}
                  </div>

                  {/* 자재거래처 — 바꾸면 바로 저장(날짜 [저장]과 별개). 스케줄 전용 공정은 자재가 없어 '해당 없음' */}
                  <div style={{ padding: '0 4px', minWidth: 0 }}>
                    {scheduleOnlyCodes.has(task.workTypeCode ?? '') ? (
                      <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)' }}>해당 없음</span>
                    ) : selectedSite?.scheduleId != null ? (
                      <CalendarVendorCell
                        taskId={task.taskId}
                        siteId={selectedSite.siteId}
                        scheduleId={selectedSite.scheduleId}
                        disabled={isSaving}
                        onChanged={handleMaterialChanged}
                      />
                    ) : null}
                  </div>

                  {/* 기간 */}
                  <div style={{ padding: '0 4px', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <DatePicker
                      value={localStart}
                      onChange={v => { if (v) { handleStartChange(task.taskId, v, task); handleDateBlur(task.taskId) } }}
                      disabled={isSaving}
                      className="!max-w-none !flex-1 !min-w-0"
                    />
                    {/* 소요일 — 좁은 칸이라 폭과 좌우 패딩만 줄이고 나머지는 ui-core .input 기본값 */}
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={workdayCount(holidayState, holidays, localStart, localEnd)}
                      onChange={e => handleDurationChange(task.taskId, Number(e.target.value), task)}
                      onBlur={() => handleDateBlur(task.taskId)}
                      disabled={isSaving}
                      className="!max-w-none !w-14 !flex-none"
                      style={{ padding: '0 6px', textAlign: 'center' }}
                    />
                    <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)', flexShrink: 0 }}>일</span>
                  </div>

                  {/* 메모 */}
                  <div style={{ padding: '0 4px' }}>
                    <Input
                      type="text"
                      value={memoVal}
                      placeholder="메모 입력"
                      maxLength={1000}
                      disabled={isSaving}
                      onChange={e => setLocalMemos(prev => ({ ...prev, [task.taskId]: e.target.value }))}
                      onBlur={() => handleMemoBlur(task.taskId)}
                      className="!max-w-none"
                    />
                  </div>

                  {/* 삭제 아이콘 */}
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button
                      onClick={() => handleDelete(task.taskId)}
                      disabled={isSaving}
                      title="삭제"
                      style={{ background: 'none', border: 'none', cursor: isSaving ? 'default' : 'pointer', padding: 4, color: 'var(--status-danger-600)', display: 'flex', alignItems: 'center', borderRadius: 4, opacity: isSaving ? 0.4 : 1 }}
                    >
                      <TrashIcon size={14} />
                    </button>
                  </div>
                </div>
              )
            })}
            {/* 마일스톤 — 공종 목록 다음에 표시 */}
            {selDayMiles.map(m => {
              const mc = MILESTONE_COLORS[m.milestoneType ?? ''] ?? MILESTONE_DEFAULT_COLOR
              const today = new Date(); today.setHours(0,0,0,0)
              const [my, mm, md] = m.milestoneDate.slice(0,10).split('-').map(Number)
              const daysUntil = Math.round((new Date(my, mm-1, md).getTime() - today.getTime()) / 86400000)
              const dxLabel = daysUntil >= 0 ? `D-${daysUntil}` : `D+${Math.abs(daysUntil)}`
              return (
                <div key={`ms-${m.milestoneId}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: `${mc}0A`, borderBottom: `1px solid ${mc}25`, borderLeft: `3px solid ${mc}` }}>
                  <span style={{ color: mc, fontSize: 13, flexShrink: 0 }}>◆</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface)', flex: 1 }}>{m.milestoneName}</span>
                  <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: mc, flexShrink: 0 }}>{dxLabel}</span>
                </div>
              )
            })}
            {/* 자재 발주일/입고예정일 — 마일스톤 다음에 표시 */}
            {selDayOrders.map(o => {
              const wtName = events.find(ev => ev.taskId === o.taskId)?.workTypeName ?? ''
              return (
                <div key={`ord-${o.taskId}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'color-mix(in srgb, var(--gt-primary) 4%, white)', borderBottom: '1px solid color-mix(in srgb, var(--gt-primary) 15%, white)', borderLeft: '3px solid var(--gt-primary)' }}>
                  <span style={{ color: 'var(--gt-primary)', fontSize: 13, flexShrink: 0 }}>◆</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface)', flex: 1 }}>{wtName} 발주</span>
                </div>
              )
            })}
            {selDayDeliveries.map(o => {
              const wtName = events.find(ev => ev.taskId === o.taskId)?.workTypeName ?? ''
              return (
                <div key={`del-${o.taskId}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'var(--shell-bg)', borderBottom: '1px solid var(--shell-border)', borderLeft: '3px solid var(--shell-on-surface)' }}>
                  <span style={{ color: 'var(--shell-on-surface)', fontSize: 13, flexShrink: 0 }}>●</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface)', flex: 1 }}>{wtName} 입고</span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  return (
    <Page layout="fixed">
      {/* Info bar */}
      <div style={{ background: 'var(--shell-surface)', padding: '0 18px', borderBottom: '1px solid var(--shell-border)', display: 'flex', alignItems: 'center', minHeight: 52, flexShrink: 0, gap: 16 }}>
        {/* 현장 정보 — 현장명(고객명) · 공사기간 · D-day 순으로 한 줄에 배치 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-.4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {selectedSite?.siteName || '—'}
            {selectedSite?.customerName && <span style={{ color: 'var(--shell-on-surface-2)', fontWeight: 600 }}>({selectedSite.customerName})</span>}
          </span>
          {selectedSite?.startDate && selectedSite?.endDate && (
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--shell-on-surface-2)', letterSpacing: '-.1px', flexShrink: 0 }}>
              {fmtDateStr(selectedSite.startDate)} ~ {fmtDateStr(selectedSite.endDate)}
            </span>
          )}
          {endDt && <Badge status="info" className="shrink-0">{dDay}</Badge>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <TodayTodoButton
            count={signal.todoCount}
            isError={signal.todosQ.isError}
            isLoading={signal.todosQ.isLoading}
            onOpen={anchor => signal.setPanel({ anchor })}
          />
          <div style={{ position: 'relative' }}>
            <Button
              variant="secondary"
              size="md"
              className="gap-1"
              onClick={e => { e.stopPropagation(); setOpenDd(openDd === 'dd2' ? null : 'dd2') }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              파트너배정 / 알림발송<span style={{ fontSize: 'var(--type-caption-size)', marginLeft: 2 }}>▼</span>
            </Button>
            {openDd === 'dd2' && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, background: 'var(--shell-surface)', border: 'var(--border-width-hairline) solid var(--shell-border)', borderRadius: 12, boxShadow: 'var(--elevation-2)', zIndex: Z_CONTEXT_MENU, width: 'max-content', minWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                <div role="button" tabIndex={0} onClick={e => { e.stopPropagation(); setShowSigongPanel(true); setOpenDd(null) }} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setShowSigongPanel(true); setOpenDd(null) } }} onMouseOver={e => (e.currentTarget.style.background = 'var(--shell-bg)')} onMouseOut={e => (e.currentTarget.style.background = '')} style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--shell-on-surface)', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--shell-border)' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--shell-on-surface)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  시공파트너배정-&gt;일정확인발송
                </div>
                <div role="button" tabIndex={0} onClick={e => { e.stopPropagation(); openMaterialPopup(null); setOpenDd(null) }} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); openMaterialPopup(null); setOpenDd(null) } }} onMouseOver={e => (e.currentTarget.style.background = 'var(--shell-bg)')} onMouseOut={e => (e.currentTarget.style.background = '')} style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--shell-on-surface)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--shell-on-surface)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                  자재파트너배정-&gt;발주정보발송
                </div>
                <div role="button" tabIndex={0} onClick={e => { e.stopPropagation(); setShowShipPanel(true); setOpenDd(null) }} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setShowShipPanel(true); setOpenDd(null) } }} onMouseOver={e => (e.currentTarget.style.background = 'var(--shell-bg)')} onMouseOut={e => (e.currentTarget.style.background = '')} style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--shell-on-surface)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--shell-on-surface)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                  배송정보현황-&gt;요청
                </div>
              </div>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <Button
              variant="secondary"
              size="md"
              className="gap-1"
              onClick={e => { e.stopPropagation(); setOpenDd(openDd === 'dd3' ? null : 'dd3') }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              작업요청/현황<span style={{ fontSize: 'var(--type-caption-size)', marginLeft: 2 }}>▼</span>
            </Button>
            {openDd === 'dd3' && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, background: 'var(--shell-surface)', border: 'var(--border-width-hairline) solid var(--shell-border)', borderRadius: 12, boxShadow: 'var(--elevation-2)', zIndex: Z_CONTEXT_MENU, minWidth: 150, overflow: 'hidden' }}>
                <div role="button" tabIndex={0} aria-disabled="true" onClick={e => { e.stopPropagation(); setOpenDd(null) }} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setOpenDd(null) } }} style={{ padding: '10px 14px', cursor: 'not-allowed', fontSize: 12, fontWeight: 600, color: 'var(--shell-hint)', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--shell-border)' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>
                  작업요청 작성
                </div>
                {/* 작업현황(작업보고) 작성 — 보고 있는 스케줄을 넘겨 작성 화면이 그 현장을 미리 고른다. 호스트 이동이 없으면 숨김 */}
                {schedulerHost?.openPath && (
                  <div role="button" tabIndex={0} onClick={e => { e.stopPropagation(); schedulerHost.openPath?.(workStatusWritePath(selectedSite?.scheduleId)); setOpenDd(null) }} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); schedulerHost.openPath?.(workStatusWritePath(selectedSite?.scheduleId)); setOpenDd(null) } }} onMouseOver={e => (e.currentTarget.style.background = 'var(--shell-bg)')} onMouseOut={e => (e.currentTarget.style.background = '')} style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--shell-on-surface)', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--shell-border)' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--shell-on-surface)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>
                    작업현황 작성
                  </div>
                )}
                <div role="button" tabIndex={0} onClick={e => { e.stopPropagation(); setShowStatusPanel(true); setOpenDd(null) }} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setShowStatusPanel(true); setOpenDd(null) } }} onMouseOver={e => (e.currentTarget.style.background = 'var(--shell-bg)')} onMouseOut={e => (e.currentTarget.style.background = '')} style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--shell-on-surface)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--shell-on-surface)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  공사현황 보기
                </div>
              </div>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <Button
              variant="primary"
              size="md"
              className="gap-1"
              onClick={e => { e.stopPropagation(); setOpenDd(openDd === 'dd1' ? null : 'dd1') }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><line x1="5" y1="1" x2="5" y2="9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><line x1="1" y1="5" x2="9" y2="5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
              공종 추가<span style={{ fontSize: 'var(--type-caption-size)', marginLeft: 2, opacity: 0.8 }}>▼</span>
            </Button>
            {openDd === 'dd1' && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, background: 'var(--shell-surface)', border: 'var(--border-width-hairline) solid var(--shell-border)', borderRadius: 12, boxShadow: 'var(--elevation-2)', zIndex: Z_CONTEXT_MENU, minWidth: 150, overflow: 'hidden' }}>
                <div role="button" tabIndex={0} onClick={e => { e.stopPropagation(); if (selectedSite) setShowAddPop(true); setOpenDd(null) }} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); if (selectedSite) setShowAddPop(true); setOpenDd(null) } }} onMouseOver={e => (e.currentTarget.style.background = 'var(--shell-bg)')} onMouseOut={e => (e.currentTarget.style.background = '')} style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--shell-on-surface)', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--shell-border)' }}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><line x1="5" y1="1" x2="5" y2="9" stroke="var(--gt-primary)" strokeWidth="1.8" strokeLinecap="round"/><line x1="1" y1="5" x2="9" y2="5" stroke="var(--gt-primary)" strokeWidth="1.8" strokeLinecap="round"/></svg>
                  공종 추가
                </div>
                <div role="button" tabIndex={0} onClick={e => { e.stopPropagation(); setShowMilestonePop(true); setOpenDd(null) }} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setShowMilestonePop(true); setOpenDd(null) } }} onMouseOver={e => (e.currentTarget.style.background = 'var(--shell-bg)')} onMouseOut={e => (e.currentTarget.style.background = '')} style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--shell-on-surface)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="10" height="10" viewBox="0 0 20 20"><polygon points="10,1 19,10 10,19 1,10" fill="var(--gt-primary)"/></svg>
                  마일스톤
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Options bar */}
      <div style={{ background: 'var(--shell-bg)', padding: '6px 18px', borderBottom: '1px solid var(--shell-border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-02)', flexShrink: 0 }}>
          <SignalToggleChips layer={signal} />
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', letterSpacing: '.3px', whiteSpace: 'nowrap' }}>보기</span>
        <Segmented
          className="!shrink-0"
          options={SCHEDULE_VIEW_OPTIONS}
          value="cal-b2"
          onChange={v => { if (v === 'gantt') navigate('gantt'); else if (v === 'work-type') navigate('work-type') }}
        />
      </div>

      {/* ── 신호 사유 팝오버 · 오늘 할일 패널 ───────────────────────────── */}
      {sigOn && signal.popover && (() => {
        const ev = events.find(e => e.taskId === signal.popover!.taskId)
        const ts = signal.byTask.get(signal.popover.taskId)
        if (!ev || !ts) return null
        return (
          <SignalPopover
            anchor={signal.popover.anchor}
            workTypeName={ev.workTypeName}
            task={ts}
            onClose={signal.closePopover}
            canAct={signalAction.canAct}
            onAct={item => { signal.closePopover(); signalAction.act(item) }}
            onOpenTodos={() => { const a = signal.popover!.anchor; signal.closePopover(); signal.setPanel({ anchor: a }) }}
            onOpenGantt={() => { signal.closePopover(); navigate('gantt', selectedSite?.scheduleId) }}
          />
        )
      })()}
      {signal.panel && (
        <SignalTodoPanel
          anchor={signal.panel.anchor}
          data={signal.todosQ.data}
          isLoading={signal.todosQ.isLoading}
          isError={signal.todosQ.isError}
          onRetry={() => { void signal.todosQ.refetch() }}
          onClose={signal.closePanel}
          siteName={selectedSite?.siteName}
          canAct={signalAction.canAct}
          onAct={item => { signal.closePanel(); signalAction.act(item) }}
          onOpenTodoPage={schedulerHost?.openTodo ? () => { signal.closePanel(); schedulerHost.openTodo?.() } : undefined}
        />
      )}

      {/* ── 시공 팝업 ──────────────────────────────────────────────── */}
      {showSigongPanel && (() => {
        const COLORS = WORK_TYPE_COLORS
        const sigongTasks: SigongTask[] = events.map((ev, i) => {
          const det = detTasks.find(t => t.taskId === ev.taskId)
          return {
            taskId:         ev.taskId,
            workTypeName:   ev.workTypeName,
            startDate:      new Date(ev.startDate),
            endDate:        new Date(ev.endDate),
            color:          ev.color ?? COLORS[i % COLORS.length],
            foremanName:    det?.foremanName ?? null,
            foremanContact: det?.foremanContact ?? null,
            partnerStatus:  det?.partnerStatus ?? '',
            respondedDtm:   det?.respondedDtm ?? null,
            sentDtm:        det?.sentDtm ?? null,
            assigneePartnerId: det?.assigneePartnerId ?? null,
            assignSource:   det?.assignSource ?? null,
            constructByVendor: det?.constructByVendorYn === 'Y',
          }
        })
        return (
          <SigongPopup
            tasks={sigongTasks}
            partners={partners}
            siteName={selectedSite?.siteName}
            scheduleId={selectedSite?.scheduleId}
            onClose={() => setShowSigongPanel(false)}
            onPartnerCreated={fetchPartners}
            onAssigned={handleForemanAssigned}
            onOpenSettings={embedded ? undefined : () => navigate('settings-foreman-confirm')}
          />
        )
      })()}

      {/* ── 자재파트너배정·발주정보발송 팝업 (C-3) ──────────────────── */}
      {showMaterialPanel && selectedSite?.scheduleId != null && (
        <MaterialOrderPopup
          siteId={selectedSite.siteId}
          scheduleId={selectedSite.scheduleId}
          siteName={selectedSite.siteName}
          focusTaskId={materialFocusTaskId}
          onClose={() => setShowMaterialPanel(false)}
          onChanged={handleMaterialChanged}
          onOpenOrderSettings={embedded ? undefined : () => navigate('settings-order')}
        />
      )}

      {/* ── 배송정보현황·요청 팝업 (C-4) — 빈 껍데기 ─────────────────── */}
      {showShipPanel && selectedSite?.scheduleId != null && (
        <ShipStatusPopup
          onClose={() => setShowShipPanel(false)}
          scheduleId={selectedSite.scheduleId}
          siteName={selectedSite.siteName}
          taskNameById={new Map(events.map(ev => [ev.taskId, ev.workTypeName] as const))}
          vendorConstructTaskIds={events.filter(ev => detTasks.find(t => t.taskId === ev.taskId)?.constructByVendorYn === 'Y').map(ev => ev.taskId)}
          onOpenForemanAssign={() => setShowSigongPanel(true)}
        />
      )}

      {/* ── 공사현황보기 팝업 (C-1-11) ──────────────────────────────── */}
      {showStatusPanel && (
        <FieldReportStatusPopup
          onClose={() => setShowStatusPanel(false)}
          siteId={selectedSite?.siteId}
          siteName={selectedSite?.siteName}
        />
      )}

      {/* ── 공종 추가 팝업 ──────────────────────────────────────────── */}
      {showAddPop && selectedSite && (
        <AddTaskPopup
          onClose={() => setShowAddPop(false)}
          selectedSite={selectedSite}
          partners={partners}
          defaultStartDate={selDay ?? undefined}
          existingWorkTypeCodes={detTasks.map(t => t.workTypeCode)}
          onSaved={() => setRefreshKey(k => k + 1)}
        />
      )}

      {/* ── 마일스톤 팝업 ────────────────────────────────────────────── */}
      {showMilestonePop && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'var(--alpha-black-500)', zIndex: Z_POPUP, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowMilestonePop(false) }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={milestoneModal.titleId}
            tabIndex={-1}
            ref={milestoneModal.boxRef}
            style={{ background: 'var(--shell-bg)', borderRadius: 24, width: 540, maxHeight: '82vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: 'var(--elevation-3)' }}
          >
            <div style={{ background: 'var(--shell-surface)', padding: '14px 20px', borderBottom: '1px solid var(--shell-border)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <span style={{ color: 'var(--gt-primary)', fontSize: 16 }}>◆</span>
              <span id={milestoneModal.titleId} style={{ fontSize: 15, fontWeight: 700, color: 'var(--shell-on-surface)', flex: 1 }}>마일스톤 관리</span>
              {selectedSite && <span style={{ fontSize: 12, color: 'var(--shell-on-surface-2)' }}>{selectedSite.siteName}</span>}
              <button onClick={() => setShowMilestonePop(false)} aria-label="닫기" style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'var(--shell-border)', color: 'var(--shell-on-surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontFamily: 'inherit', flexShrink: 0, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }}>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: 'var(--gt-primary)' }}>◆</span>
                  마일스톤 목록
                  <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 500, color: 'var(--shell-hint)' }}>({milestones.length}개)</span>
                </div>
                {milestones.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--shell-hint)', fontSize: 12, background: 'var(--shell-surface)', borderRadius: 12, border: '1px dashed var(--shell-border)' }}>
                    등록된 마일스톤이 없습니다<br />
                    <span style={{ fontSize: 'var(--type-caption-size)' }}>아래에서 새 마일스톤을 추가하세요</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[...milestones].sort((a, b) => a.milestoneDate.localeCompare(b.milestoneDate)).map(m => {
                      const color = MILESTONE_COLORS[m.milestoneType ?? ''] ?? MILESTONE_DEFAULT_COLOR
                      const mDate = new Date(m.milestoneDate); mDate.setHours(0, 0, 0, 0)
                      const tDate = new Date(); tDate.setHours(0, 0, 0, 0)
                      const past  = mDate < tDate
                      const diff  = Math.round((mDate.getTime() - tDate.getTime()) / 86400000)
                      const dday  = diff > 0 ? `D-${diff}` : diff === 0 ? 'D-Day' : `D+${Math.abs(diff)}`
                      const [y, mo, d] = m.milestoneDate.split('-')
                      const isEditing = editingMsId === m.milestoneId
                      return (
                        <div key={m.milestoneId} style={{ background: 'var(--shell-surface)', border: `var(--border-width-hairline) solid ${isEditing ? color : `color-mix(in srgb, ${color} 25%, transparent)`}`, borderRadius: 12, padding: '12px 14px', opacity: (!isEditing && past) ? 0.65 : 1 }}>
                          {isEditing ? (
                            <>
                              <div style={{ marginBottom: 8 }}>
                                <label style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', display: 'block', marginBottom: 4 }}>유형 <span style={{ color: 'var(--shell-hint)', fontWeight: 400 }}>(자주 쓰는 유형)</span></label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                  {FREQUENT_MILESTONE_TYPES.map(code => {
                                    const opt = milestoneTypeOptions.find((o: { typeCode: string; typeName: string }) => o.typeCode === code) as { typeCode: string; typeName: string } | undefined
                                    const name = opt?.typeName ?? DEFAULT_MILESTONE_TYPE_NAMES[code] ?? code
                                    const active = mileEditForm.milestoneType === code
                                    const chipColor = MILESTONE_COLORS[code] ?? 'var(--shell-on-surface-2)'
                                    return (
                                      <button
                                        key={code}
                                        type="button"
                                        onClick={() => setMileEditForm(f => ({ ...f, milestoneType: code, milestoneName: name }))}
                                        style={{
                                          display: 'flex', alignItems: 'center', gap: 4,
                                          padding: '4px 10px', borderRadius: 24, fontSize: 'var(--type-caption-size)', fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
                                          border: `var(--border-width-hairline) solid ${active ? chipColor : 'var(--shell-border)'}`,
                                          background: active ? `color-mix(in srgb, ${chipColor} 12%, white)` : 'var(--shell-bg)',
                                          color: active ? chipColor : 'var(--shell-on-surface-2)',
                                        }}
                                      >
                                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: chipColor, flexShrink: 0 }} />
                                        {name}
                                      </button>
                                    )
                                  })}
                                </div>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                                <div>
                                  <label style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', display: 'block', marginBottom: 3 }}>날짜 <span style={{ color: 'var(--gt-primary)' }}>*</span></label>
                                  <DatePicker value={mileEditForm.milestoneDate} onChange={v => setMileEditForm(f => ({ ...f, milestoneDate: v }))} size="sm" className="!max-w-none" />
                                </div>
                                <div>
                                  <label style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', display: 'block', marginBottom: 3 }}>라벨</label>
                                  <Input type="text" value={mileEditForm.milestoneName} onChange={e => setMileEditForm(f => ({ ...f, milestoneName: e.target.value, milestoneType: null }))} placeholder="비우면 유형명 사용" size="sm" className="!max-w-none" />
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                <Button variant="secondary" size="sm" className="!h-auto !px-3 !py-[5px] text-[length:var(--type-caption-size)]" onClick={() => { setEditingMsId(null); setMileFormError('') }}>취소</Button>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  className="!h-auto !px-3.5 !py-[5px] text-[length:var(--type-caption-size)]"
                                  style={{ background: color, borderColor: color }}
                                  loading={mileEditing}
                                  onClick={() => handleMileEditSave(m.milestoneId)}
                                >{mileEditing ? '저장 중...' : '저장'}</Button>
                              </div>
                            </>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ color, fontSize: 18, flexShrink: 0 }}>◆</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface)', marginBottom: 2 }}>{m.milestoneName}</div>
                                <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                  <span>{y}년 {Number(mo)}월 {Number(d)}일</span>
                                </div>
                              </div>
                              <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: past ? 'var(--shell-on-surface-2)' : color, background: past ? 'var(--shell-skeleton)' : `color-mix(in srgb, ${color} 15%, transparent)`, padding: '3px 9px', borderRadius: 24, flexShrink: 0 }}>{dday}</span>
                              <Button
                                variant="secondary"
                                size="sm"
                                className="!h-auto shrink-0 !px-2.5 !py-[3px] text-[length:var(--type-caption-size)]"
                                style={{ borderColor: `color-mix(in srgb, ${color} 50%, transparent)`, color }}
                                onClick={() => handleMileEditOpen(m)}
                              >수정</Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                className="!h-auto shrink-0 !px-2.5 !py-[3px] text-[length:var(--type-caption-size)]"
                                style={{ borderColor: 'color-mix(in srgb, var(--color-error) 25%, white)', color: 'var(--status-danger-600)' }}
                                loading={mileDeleting === m.milestoneId}
                                onClick={() => handleMileDelete(m.milestoneId)}
                              >{mileDeleting === m.milestoneId ? '' : '삭제'}</Button>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: 'var(--gt-primary)' }}>◆</span>
                  새 마일스톤 추가
                </div>

                {/* 유형: 자주 쓰는 유형을 칩으로 노출 — 선택 시 라벨에 그대로 반영. 라벨을 직접 수정하면
                    어떤 칩도 선택되지 않은 상태로 바뀐다. */}
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', display: 'block', marginBottom: 6 }}>유형 <span style={{ color: 'var(--shell-hint)', fontWeight: 400 }}>(자주 쓰는 유형)</span></label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {FREQUENT_MILESTONE_TYPES.map(code => {
                      const opt = milestoneTypeOptions.find((o: { typeCode: string; typeName: string }) => o.typeCode === code) as { typeCode: string; typeName: string } | undefined
                      const name = opt?.typeName ?? DEFAULT_MILESTONE_TYPE_NAMES[code] ?? code
                      const active = mileForm.milestoneType === code
                      const color = MILESTONE_COLORS[code] ?? 'var(--shell-on-surface-2)'
                      return (
                        <button
                          key={code}
                          type="button"
                          onClick={() => setMileForm(f => ({ ...f, milestoneType: code, milestoneName: name }))}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            padding: '6px 12px', borderRadius: 24, fontSize: 'var(--type-caption-size)', fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
                            border: `var(--border-width-hairline) solid ${active ? color : 'var(--shell-border)'}`,
                            background: active ? `color-mix(in srgb, ${color} 12%, white)` : 'var(--shell-surface)',
                            color: active ? color : 'var(--shell-on-surface-2)',
                          }}
                        >
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
                          {name}
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', display: 'block', marginBottom: 4 }}>날짜 <span style={{ color: 'var(--gt-primary)' }}>*</span></label>
                  <DatePicker value={mileForm.milestoneDate} onChange={v => setMileForm(f => ({ ...f, milestoneDate: v }))} className="!max-w-none" />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', display: 'block', marginBottom: 4 }}>라벨 <span style={{ color: 'var(--shell-hint)' }}>(직접 입력하면 입력한 라벨 그대로 등록)</span></label>
                  <Input type="text" value={mileForm.milestoneName} onChange={e => setMileForm(f => ({ ...f, milestoneName: e.target.value, milestoneType: null }))} placeholder="예: 2차 세대 입주 예정" className="!max-w-none" />
                </div>
                {mileFormError && <div className="field error" style={{ marginBottom: 10, maxWidth: 'none' }} role="alert"><span className="help">{mileFormError}</span></div>}
                <Button
                  variant="primary"
                  size="sm"
                  className="w-full gap-1.5"
                  disabled={isMileInvalid}
                  loading={mileSaving}
                  onClick={handleMileAdd}
                >
                  <span style={{ fontSize: 13 }}>◆</span>
                  {mileSaving ? '저장 중...' : '마일스톤 추가'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content — D-07 §17.1 상태 전환: Loading → Error / Empty / Content */}
      {!selectedSite ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EmptyState title="현장을 선택해주세요" description="오른쪽 패널에서 현장 카드를 클릭하세요" />
        </div>
      ) : loading ? (
        // 달력 그리드를 모사한다 — "불러오는 중" 텍스트·스피너 단독 금지(§15.37)
        <div aria-busy="true" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, padding: 24 }}>
          {[0, 1, 2, 3, 4].map(i => <Skeleton key={i} height={64} />)}
        </div>
      ) : loadError ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <ErrorState description="캘린더 정보를 불러올 수 없어요. 잠시 후 다시 시도해주세요." onRetry={() => setRefreshKey(k => k + 1)} />
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          {renderMonth()}
          {renderBottomPanel()}
        </div>
      )}

      {/* 하단 비고 컴포넌트 */}
      {selectedSite && !loading && (
        <div style={{ background: 'var(--shell-bg)', borderTop: '1px solid var(--shell-border)', padding: '10px 16px 12px', flexShrink: 0 }}>
          <div style={{ background: 'var(--shell-surface)', border: 'var(--border-width-hairline) solid var(--shell-border)', borderRadius: 12, overflow: 'hidden', boxShadow: 'var(--elevation-1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid var(--shell-bg)', background: 'var(--shell-bg)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface)' }}>비고</span>
                <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>현장 전체 일정에 대한 기록입니다.</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {!remarkHidden && <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>{remarkDraft.length} / 500</span>}
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1"
                  onClick={() => setRemarkHidden(h => !h)}
                >
                  {remarkHidden ? (
                    <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>펼치기</>
                  ) : (
                    <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>숨기기</>
                  )}
                </Button>
              </div>
            </div>
            {!remarkHidden && (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, padding: '10px 12px' }}>
                <Textarea
                  value={remarkDraft}
                  maxLength={500}
                  onChange={e => setRemarkDraft(e.target.value)}
                  placeholder="예) 6/18 오전 엘리베이터 사용 불가, 자재 반입은 오후 2시 이후 가능"
                  className="!max-w-none flex-1"
                  style={{ minHeight: 54, maxHeight: 96 }}
                />
                <Button
                  variant="primary"
                  size="sm"
                  className="whitespace-nowrap"
                  disabled={remarkDraft === scheduleRemark}
                  loading={remarkSaving}
                  onClick={handleRemarkSave}
                >{remarkSaving ? '저장 중...' : '비고 저장'}</Button>
              </div>
            )}
          </div>
        </div>
      )}

      {foremanAssign.modal}
      {periodSync.modal}

      {/* ── 일정 변경 카톡 확인 창(날짜 저장 직후, 확정·회신 대기 반장 대상) ─────── */}
      {changeNotice && (
        <ScheduleChangeNoticeModal
          scheduleId={changeNotice.scheduleId}
          changes={changeNotice.changes}
          colorOf={(taskId) => events.find(e => e.taskId === taskId)?.color}
          onSent={handleChangeNoticeSent}
          onClose={() => setChangeNotice(null)}
        />
      )}

      {/* ── 당기기 확인 다이얼로그(후행공정 함께 이동 여부) ─────────── */}
      <Modal
        open={pullConfirm != null}
        onClose={() => setPullConfirm(null)}
        title="후행공정도 함께 이동할까요?"
        footerAlign="stretch"
        footer={pullConfirm && (
          <>
            <Button variant="secondary" onClick={() => resolvePullConfirm('self')}>이 공종만 이동</Button>
            <Button variant="primary" onClick={() => resolvePullConfirm('cascade')}>후행공종도 함께 이동</Button>
          </>
        )}
      >
        {pullConfirm && (
          <>
            <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', lineHeight: 1.6 }}>
              {pullConfirm.taskName}의 시작일을 앞으로 당겼습니다. 후행공정도 함께 앞당길까요, 아니면 이 공종만 이동할까요?
            </div>
          </>
        )}
      </Modal>
    </Page>
  )
}
